import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetZohoOwnersQuery,
	useGetZohoUserMappingsQuery,
	useCreateZohoUserMappingMutation,
	useDeleteZohoUserMappingMutation,
} from "@/pages/Private/redux/zoho/zoho.api";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

interface LocalMapping {
	userId: number;
	zohoOwnerId: string;
}

export const ZohoUserMappingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.zoho.userMapping.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: sendersData } = useGetSendersUsersQuery();
	const { data: ownersData } = useGetZohoOwnersQuery();
	const { data: userMappingsData, isLoading: isLoadingMappings } = useGetZohoUserMappingsQuery();
	const [createUserMapping] = useCreateZohoUserMappingMutation();
	const [deleteUserMapping] = useDeleteZohoUserMappingMutation();

	const [mappings, setMappings] = useState<LocalMapping[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (userMappingsData) {
			setMappings(userMappingsData.map((m) => ({ userId: m.userId, zohoOwnerId: m.zohoOwnerId })));
		}
	}, [userMappingsData]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => {
				const name = o.fullName ?? `${o.firstName || ""} ${o.lastName || ""}`.trim();

				return {
					id: o.id,
					title: name ? `${name} (${o.email})` : o.email,
				};
			}) || [],
		[ownersData]
	);

	const handleOwnerChange = (userId: number, ownerId: string) => {
		setMappings((prev) => {
			const existing = prev.find((m) => m.userId === userId);

			if (existing) {
				return prev.map((m) => (m.userId === userId ? { ...m, zohoOwnerId: ownerId } : m));
			}

			return [...prev, { userId, zohoOwnerId: ownerId }];
		});
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			const serverMappings = userMappingsData || [];

			// Find mappings to delete (on server but not in local state, or owner changed)
			const toDelete = serverMappings.filter((sm) => {
				const local = mappings.find((m) => m.userId === sm.userId);

				return !local || local.zohoOwnerId !== sm.zohoOwnerId;
			});

			// Find mappings to create (in local state but not on server, or owner changed)
			const toCreate = mappings.filter((m) => {
				const server = serverMappings.find((sm) => sm.userId === m.userId);

				return !server || server.zohoOwnerId !== m.zohoOwnerId;
			});

			// Delete removed/changed mappings first
			await Promise.all(toDelete.map((m) => deleteUserMapping(m.id).unwrap()));

			// Create new/changed mappings
			await Promise.all(
				toCreate.map((m) =>
					createUserMapping({ userId: m.userId, zohoOwnerId: m.zohoOwnerId }).unwrap()
				)
			);

			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		} finally {
			setIsSaving(false);
		}
	};

	const senders = sendersData?.data || [];

	if (isLoadingMappings) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-gray-600 mb-1">{ts("description")}</div>

			{senders.length === 0 ? (
				<div className="py-6 text-center text-gray-500">{ts("noUsers")}</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-4 py-2 border-b border-gray-200 font-medium text-sm text-gray-600">
						<div className="flex-1">{ts("signalistUser")}</div>
						<div className="flex-1">{ts("zohoOwner")}</div>
					</div>

					{senders.map((user) => {
						const currentMapping = mappings.find((m) => m.userId === user.id);
						const defaultOwner = currentMapping
							? ownerOptions.find((o) => o.id === currentMapping.zohoOwnerId)
							: undefined;

						return (
							<div key={user.id} className="flex items-center gap-4 py-2 border-b border-gray-100">
								<div className="flex-1 text-sm">
									{user.firstName} {user.lastName} ({user.email})
								</div>
								<div className="flex-1">
									<Dropdown
										data={ownerOptions}
										defaultValue={defaultOwner}
										floating={true}
										handleSelect={(value) => {
											handleOwnerChange(user.id, value.id as string);
										}}
										label=""
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
